import chroma from 'chroma-js';
import * as d3 from 'd3';
import {clamp, isEqual, uniq, uniqWith} from 'lodash';
import React from 'react';

import {LegendConfig, makeLine, makeRect} from 'src/components/Legend';
import {Request, Props as TimeSeriesProviderProps} from 'src/components/TimeSeriesProvider';
import {Translator} from 'src/components/TranslationsContext';
import {Site} from 'src/lib/api/project';
import {
  AggregationOptions,
  AllColumns,
  ApiForecastTimeSeries,
  FlowForecastColumns,
  ForecastTimeSeriesQuery,
  ForecastTimesBySource,
  ObservationTimeSeriesQuery,
  Source,
  UnitSystem,
  WeatherColumns,
} from 'src/lib/api/series';
import COLORS, {getColor, getHfColors} from 'src/lib/colors';
import {forecastReturnedColumns} from 'src/lib/dataUtils';
import {ConfigBySourceId, PRECIP_FILL_OPACITY, WEATHER_SOURCES} from 'src/lib/sourceConfigs';

export type VizConfigCreatorItem = {
  displayName: string;
  source: Source;
  columns: AllColumns[];
  getStyle: (idx?: number, length?: number) => VizConfigStyle;
  legendIcon: 'line' | 'rect';
  curveFactory?: d3.CurveFactory;
  groupId?: string;
  legendOptions?: Partial<Omit<LegendConfig, 'makeIcon'>>;
  showForecastTimeMarkers?: boolean | undefined;
  hideIfMultipleForecastTimes?: boolean | undefined;
  hideTooltip?: boolean | undefined;
  showColumnInTooltipDisplayName?: boolean | undefined;

  // when a viz loads, this time series will default to being checked *off* in
  // the legend, and therefore not visible on the map
  defaultNotVisible?: boolean;

  // this hides the time series completely - it will not show up in the legend
  // nor anywhere in viz. This is useful if you want to load a time series, say
  // for csv export (confidence intervals for example), but not show it anywhere.
  isHidden?: boolean;

  aggregationOptions?: AggregationOptions;
};

export type VizConfigStyle = (
  | React.SVGProps<SVGRectElement>
  | React.SVGProps<SVGLineElement>
  | React.SVGProps<SVGCircleElement>
) & {hfStrokeOutlineStyle?: React.SVGProps<SVGLineElement>};

export type VizConfigCreator = {
  forecasts: VizConfigCreatorItem[];
  observations: VizConfigCreatorItem[];
};

export type VizConfigItem = Omit<VizConfigCreatorItem, 'legendIcon'> & {
  id: string;
  type: 'forecasts' | 'observations';
  legend: LegendConfig;
};

export type VizConfig = {
  forecasts: VizConfigItem[];
  observations: VizConfigItem[];
};

export function makeApiQueries(
  forecastTimesBySource: ForecastTimesBySource,
  dateRange: [Date, Date],
  config: VizConfig,
  site: Site,
  unitSystem: UnitSystem
): {
  observationQueries: ObservationTimeSeriesQuery[];
  forecastQueries: ForecastTimeSeriesQuery[];
} {
  const reducer = (acc: Request, c: VizConfigItem) => {
    const prevColumns = acc[c.source]?.columns || [];
    return {
      ...acc,
      [c.source]: {
        columns: uniq([...prevColumns, ...c.columns]),
        aggregationOptions: c.aggregationOptions,
      },
    };
  };
  const requestedForecastColumnsBySource = config.forecasts.reduce(reducer, {});
  const requestedObservationColumnsBySource = config.observations.reduce(reducer, {});
  const forecastQueries = uniqWith(Object.values(site.forecastTimeSeries).flat(), isEqual)
    .filter(
      (q) => !!requestedForecastColumnsBySource[q.source] && !!forecastTimesBySource[q.source]
    )
    .map((q) => {
      const {columns, aggregationOptions} = requestedForecastColumnsBySource[q.source]!;
      const query = {
        ...q,
        ...aggregationOptions,
        unitSystem: unitSystem,
        initializationTimes: forecastTimesBySource[q.source]?.map((ft) => ft.initializationTime),
      };

      if (columns) {
        query['columns'] = columns;
      }

      return query;
    });

  const observationQueries = uniqWith(Object.values(site.observationTimeSeries).flat(), isEqual)
    .filter((q) => !!requestedObservationColumnsBySource[q.source])
    .map((q) => {
      const {columns, aggregationOptions} = requestedObservationColumnsBySource[q.source]!;

      const query = {
        ...q,
        ...aggregationOptions,
        unitSystem: unitSystem,
        startDate: dateRange[0],
        endDate: dateRange[1],
      };

      if (columns) {
        query['columns'] = columns;
      }

      return query;
    });

  return {observationQueries, forecastQueries};
}

export function makeRequestFromVizConfig(
  config: VizConfig
): Pick<
  TimeSeriesProviderProps,
  'requestedForecastColumnsBySource' | 'requestedObservationColumnsBySource'
> {
  const reducer = (acc: Request, c: VizConfigItem) => {
    const prevColumns = acc[c.source]?.columns || [];
    return {
      ...acc,
      [c.source]: {
        columns: uniq([...prevColumns, ...c.columns]),
        aggregationOptions: c.aggregationOptions,
      },
    };
  };
  return {
    requestedForecastColumnsBySource: config.forecasts.reduce(reducer, {}),
    requestedObservationColumnsBySource: config.observations.reduce(reducer, {}),
  };
}

export function filterConfigToAvailableTimeSeries(config: VizConfig, site: Site) {
  const siteForecastSources = uniqWith(Object.values(site.forecastTimeSeries).flat(), isEqual)
    .flat()
    .map((q) => q.source);
  const siteObservationSources = uniqWith(Object.values(site.observationTimeSeries).flat(), isEqual)
    .flat()
    .map((q) => q.source);
  return {
    forecasts: config.forecasts.filter((c) => siteForecastSources.includes(c.source)),
    observations: config.observations.filter((c) => siteObservationSources.includes(c.source)),
  };
}

export function makeConfigItems(
  type: 'forecasts' | 'observations',
  configItems: VizConfigCreatorItem[],
  commonAggregationOptions: AggregationOptions = {}
): VizConfigItem[] {
  return configItems.map((item) => {
    const makeIcon = (idx: number, length: number) => {
      const style = item.getStyle(idx, length);
      const legendIcon = item.legendIcon;

      let icon;
      if (legendIcon === 'line') {
        const {
          strokeWidth,
          hfStrokeOutlineStyle: _,
          ...rest
        } = style as React.SVGProps<SVGLineElement> & Pick<VizConfigStyle, 'hfStrokeOutlineStyle'>;
        const legendStrokeWidth = Math.max(Number(strokeWidth) || 0, 2);
        icon = makeLine({...rest, strokeWidth: legendStrokeWidth});
      } else if (legendIcon === 'rect') {
        const {hfStrokeOutlineStyle: _, ...rectStyle} = style as React.SVGProps<SVGRectElement> &
          Pick<VizConfigStyle, 'hfStrokeOutlineStyle'>;
        const fill = (rectStyle.fill as any) || (rectStyle.stroke as any);
        icon = makeRect({...rectStyle, fill});
      }
      return <g>{icon}</g>;
    };

    const id = `${type}-${item.source}-${item.columns.join('_')}`;
    return {
      ...item,
      id,
      type,
      legend: {
        makeIcon,
        id,
        display: item.displayName,
        legendIcon: item.legendIcon,
        ...item.legendOptions,
      },
      aggregationOptions: {
        ...commonAggregationOptions,
        ...item.aggregationOptions,
      },
    };
  });
}

export function hfGetStyle(idx: number = 0, length: number = 0): VizConfigStyle {
  let strokeColor;
  if (length === 0) {
    const hfColors = getHfColors(0);
    strokeColor = hfColors[hfColors.length - 1];
  } else {
    const hfColors = getHfColors(length);
    strokeColor = hfColors[idx];
  }
  return {stroke: strokeColor, strokeWidth: 2};
}

// Note that level percentiles are exceedence likelihoods.
// So P5 = 5% likelihood of exceeding this value, which maps to the q0.95 column.
// (Aka: this mapping is not backwards!)
const LEVEL_PERCENTILES = [
  {label: 'P5', column: FlowForecastColumns.DISCHARGE_0_95, strokeMix: 0.6},
  {label: 'P25', column: FlowForecastColumns.DISCHARGE_0_75, strokeMix: 0.4},
  {label: 'P75', column: FlowForecastColumns.DISCHARGE_0_25, strokeMix: 0.2},
  {label: 'P95', column: FlowForecastColumns.DISCHARGE_0_05, strokeMix: 0.0},
] as const;

export function makeLevelPercentileConfigs({
  source,
  curveFactory,
  baseColor,
  displayNamePrefix,
  legendGroup,
  onlyShowIfExpanded,
  defaultNotVisible,
}: {
  source: Source;
  curveFactory: d3.CurveFactory;
  baseColor: string;
  displayNamePrefix: string;
  legendGroup?: string;
  onlyShowIfExpanded?: boolean;
  defaultNotVisible?: boolean;
}): VizConfigCreatorItem[] {
  return LEVEL_PERCENTILES.map(({label, column, strokeMix}) => ({
    displayName: `${displayNamePrefix} ${label}`,
    source,
    columns: [column],
    getStyle: () => ({
      stroke: chroma.mix(baseColor, '#fff', strokeMix).hex(),
      fill: baseColor,
      fillOpacity: 0.2,
    }),
    legendIcon: 'line' as const,
    curveFactory,
    hideIfMultipleForecastTimes: true,
    ...(legendGroup !== undefined && {
      legendOptions: {
        shortDisplayName: label,
        group: legendGroup,
        ...(onlyShowIfExpanded && {onlyShowIfExpanded}),
      },
    }),
    ...(defaultNotVisible && {defaultNotVisible}),
  }));
}

const DISCHARGE_QUANTILE_RE = /^discharge_q([0-9.]+)$/;

// The symmetric quantile pairs the columns hold, narrowest first.
export function findCIBands(
  availableColumns: AllColumns[]
): {lo: AllColumns; hi: AllColumns; ciPct: number}[] {
  const quantiles = availableColumns.flatMap((col) => {
    const match = col.match(DISCHARGE_QUANTILE_RE);
    return match ? [{col, q: Number(match[1])}] : [];
  });

  // Key by a rounded quantile so the symmetric-pair lookup is O(1) and float-safe
  // (e.g. 1 - 0.05 isn't exactly 0.95).
  const quantileKey = (q: number) => Math.round(q * 1000);
  const columnByQuantile = new Map(quantiles.map(({col, q}) => [quantileKey(q), col]));

  return quantiles
    .filter(({q}) => q > 0 && q < 0.5)
    .flatMap(({col, q}) => {
      const upper = columnByQuantile.get(quantileKey(1 - q));
      return upper ? [{lo: col, hi: upper, ciPct: Math.round((1 - 2 * q) * 100)}] : [];
    })
    .sort((a, b) => a.ciPct - b.ciPct);
}

/**
 * Builds HydroForecast CI band configs from whatever quantile columns a forecast
 * actually returned, rather than assuming a fixed set. Used as a fallback when the
 * standard 50%/90% bands can't be built (e.g. H5 returns a narrower column set).
 *
 * Quantile columns are named `discharge_q<Q>`, and a CI band is the symmetric pair
 * `(q, 1−q)` around the median — so the band width is derivable: q0.1/q0.9 → 80% CI,
 * q0.25/q0.75 → 50% CI, q0.05/q0.95 → 90% CI. Bands are ordered narrowest-first to
 * match the existing 50%-before-90% draw order, and styled along a continuous
 * `chroma` ramp anchored so the standard widths (50% → 0.6, 90% → 0.3 opacity)
 * reproduce today's look.
 */
export function makeHFCIBandConfigs({
  source,
  availableColumns,
  curveFactory,
  displayNamePrefix,
  loColor,
  hiColor,
  shownCIPcts,
  legendGroup,
}: {
  source: Source;
  availableColumns: AllColumns[];
  curveFactory: d3.CurveFactory;
  displayNamePrefix: string;
  loColor: string;
  hiColor: string;
  // Bands at other widths are left off the chart and the legend; all are shown when omitted.
  shownCIPcts?: number[];
  legendGroup?: string;
}): VizConfigCreatorItem[] {
  const bands = findCIBands(availableColumns);

  // Anchored to the standard widths: ciPct 50 → loColor @ 0.6, ciPct 90 → hiColor @ 0.3.
  const fillScale = chroma.scale([loColor, hiColor]).domain([50, 90]);
  const fillOpacityFor = (ciPct: number) => clamp(0.6 - ((ciPct - 50) / 40) * 0.3, 0.15, 0.6);

  return bands.map(({lo, hi, ciPct}) => {
    const shown = !shownCIPcts || shownCIPcts.includes(ciPct);
    return {
      displayName: `${displayNamePrefix} ${ciPct}% CI`,
      source,
      columns: [lo, hi] as AllColumns[],
      getStyle: () => ({
        fill: fillScale(ciPct).hex(),
        fillOpacity: fillOpacityFor(ciPct),
      }),
      legendIcon: 'rect' as const,
      hideIfMultipleForecastTimes: true,
      curveFactory,
      ...(!shown && {isHidden: true}),
      ...(legendGroup !== undefined && {
        legendOptions: {
          shortDisplayName: `${ciPct}% CI`,
          group: legendGroup,
        },
      }),
    };
  });
}

const STANDARD_CI_COLUMNS: AllColumns[] = [
  FlowForecastColumns.DISCHARGE_0_25,
  FlowForecastColumns.DISCHARGE_0_75,
  FlowForecastColumns.DISCHARGE_0_05,
  FlowForecastColumns.DISCHARGE_0_95,
];

/**
 * Post-fetch refinement of an HF short-term config. The hard-coded 50%/90% CI bands
 * need q0.25/q0.75 and q0.05/q0.95, but H5 returns a narrower column set. We can only
 * know which columns actually came back from the response data, not from the config
 * that built the request — so this runs after the data is fetched.
 *
 * When the response did return the standard columns we leave the config untouched (no
 * customer-visible change). Otherwise we replace the hard-coded HF CI bands with
 * whatever symmetric bands the returned quantiles support, styled on a chroma ramp.
 * Only meaningful in rate mode; level mode renders its own percentile lines, so
 * callers should skip it there.
 */
export function withDynamicHFCIBands(
  config: VizConfig,
  // Undefined while the forecast query is still loading — TimeSeriesProvider passes
  // `data!`, whose non-null assertion doesn't hold until the response arrives.
  forecastTimeSeriesList: ApiForecastTimeSeries[] | undefined,
  aggregationOptions: AggregationOptions
): VizConfig {
  const hfSeries = forecastTimeSeriesList?.find(
    (ts) => ts.query.source === Source.HydroForecastShortTerm
  );
  // Data not loaded yet — leave the standard config in place (its empty bands are
  // filtered out downstream until the response arrives).
  if (!hfSeries) {
    return config;
  }

  // Which columns actually came back — from the forecast data, not `ts.variables`
  // (see forecastReturnedColumns for why).
  const returnedColumns = forecastReturnedColumns(hfSeries);
  // Nothing loaded yet, or the standard CI columns are present — leave config as-is.
  if (returnedColumns.size === 0) {
    return config;
  }
  if (STANDARD_CI_COLUMNS.every((c) => returnedColumns.has(c))) {
    return config;
  }

  // An HF CI band is a two-column rect config; median/mean are single-column lines
  // and every other source is left untouched.
  const isHFBand = (c: VizConfigItem) =>
    c.source === Source.HydroForecastShortTerm && c.columns.length === 2;
  const curveFactory = config.forecasts.find(
    (c) => c.source === Source.HydroForecastShortTerm
  )?.curveFactory;

  const dynamicBands = makeConfigItems(
    'forecasts',
    makeHFCIBandConfigs({
      source: Source.HydroForecastShortTerm,
      availableColumns: [...returnedColumns] as AllColumns[],
      curveFactory: curveFactory ?? d3.curveStepAfter,
      displayNamePrefix: 'HydroForecast',
      loColor: COLORS.HF_50,
      hiColor: COLORS.HF_90,
    }),
    aggregationOptions
  );

  // Bands first so they paint behind the median/mean lines, as in the standard config.
  return {
    ...config,
    forecasts: [...dynamicBands, ...config.forecasts.filter((c) => !isHFBand(c))],
  };
}

// One precipitation and one temperature time series for a weather source, colored by its place
// in WEATHER_SOURCES so every weather chart shows a source the same way.
export function makeWeatherForecastItems(
  source: Source,
  curveFactory: d3.CurveFactory,
  t: Translator
): VizConfigCreatorItem[] {
  const color = getColor(`WEATHER_${WEATHER_SOURCES.indexOf(source) + 1}`);
  const displayName = `${ConfigBySourceId[source].display} ${t('forecast')}`;
  return [
    {
      displayName,
      source,
      columns: [WeatherColumns.PRECIPITATION],
      getStyle: () => ({stroke: color, fill: color, fillOpacity: PRECIP_FILL_OPACITY}),
      legendIcon: 'rect',
      groupId: t('precipitation'),
      showForecastTimeMarkers: true,
      curveFactory,
    },
    {
      displayName,
      source,
      columns: [WeatherColumns.TEMPERATURE],
      getStyle: () => ({stroke: color}),
      legendIcon: 'line',
      groupId: t('temperature'),
      showForecastTimeMarkers: true,
      curveFactory,
    },
  ];
}
